import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Outlet, Link, useLocation } from "react-router-dom";
import {
  ShoppingBag, Gauge, User, Loader2, Wallet, ChevronDown, ChevronLeft, CheckCircle2, Info, BookOpen, QrCode, Check,
  AlertTriangle, ChevronRight, LogOut, FileText, MessageCircle, Printer,
  Download, Share, Plus, WifiOff, X, CalendarDays, ArrowRight, Shield,
} from "lucide-react";
import { CrisClienteBrand, BottomWaves, CC } from "../../components/portal/CrisClienteBrand";
import {
  P, PortalHeader, HighlightCard, Card, RowLink, Badge, riskTone, instTone, SegmentTag, BottomNav, NAV_SPACE,
} from "../../components/portal/PortalUI";
import ScoreScreen from "../../components/portal/score/ScoreScreen";
import { PixModal } from "../../components/portal/PixModal";
import { PaymentCenter } from "../../components/portal/PaymentCenter";
import { papi, savePortalToken, clearPortalToken, hasPortalToken } from "../../lib/portalApi";
import { brl, formatDate, formatErr } from "../../lib/api";
import {
  onInstallAvailability, promptInstall, isStandalone, isIOS, isInApp,
  needsFullReloadForInstall, reloadIntoPortalAccess,
  setPortalBusy, checkWaitingUpdate, applyWaitingUpdate,
} from "../../lib/portalPwa";
import { usePortalPresence } from "../../lib/portalPresence";

const PURPLE = "#4C2380";
const LILAC = "#D9C4F0";

/* ----------------------------------------------------------- helpers */
function maskCPF(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  return d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}
function maskBirth(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 8);
  return d.replace(/(\d{2})(\d)/, "$1/$2").replace(/(\d{2})(\d)/, "$1/$2");
}
/* ----------------------------------------------------------- PWA: instalar */
function InstallButton({ variant = "solid" }) {
  const [canInstall, setCanInstall] = useState(false);
  const [showIOS, setShowIOS] = useState(false);
  const standalone = isStandalone();

  useEffect(() => onInstallAvailability(setCanInstall), []);
  if (standalone) return null; // ja instalado -> oculta a chamada

  const ios = isIOS();
  const inApp = isInApp();
  // So mostra quando ha como instalar: prompt nativo, orientacao manual (iOS/in-app) ou recarga no manifest do Portal.
  if (!canInstall && !ios && !inApp && !needsFullReloadForInstall()) return null;
  const handle = async () => {
    if (needsFullReloadForInstall()) { reloadIntoPortalAccess(); return; } // descarta manifest anterior
    if (canInstall) { await promptInstall(); return; }
    setShowIOS(true); // iOS ou prompt nativo indisponivel -> orientacao
  };
  const brand = variant === "brand";
  const base = variant === "solid" ? "text-white" : "border-2";
  const style = variant === "solid"
    ? { background: PURPLE }
    : { borderColor: LILAC, color: PURPLE };

  return (
    <>
      {variant === "row" ? (
        <RowLink onClick={handle} icon={Download} title="Instalar aplicativo" sub="Use o Cris Cliente direto da tela inicial" testid="portal-install-btn" />
      ) : brand ? (
        <button type="button" onClick={handle} data-testid="portal-install-btn"
          className="flex h-14 w-full items-center justify-center gap-3 rounded-xl border-[1.5px] bg-white/70 text-[16px] font-semibold transition-colors hover:bg-[#F3EDFA] active:bg-[#EADFF7]"
          style={{ borderColor: CC.brand, color: CC.brand, fontFamily: CC.sans }}>
          <Download size={20} strokeWidth={1.8} /> Instalar aplicativo
        </button>
      ) : (
        <button onClick={handle} data-testid="portal-install-btn"
          className={`flex w-full items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold ${base}`} style={style}>
          <Download size={16} /> Instalar aplicativo
        </button>
      )}
      {showIOS && <IOSInstallSheet ios={ios} inApp={inApp} onClose={() => setShowIOS(false)} />}
    </>
  );
}

function IOSInstallSheet({ ios, inApp, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose} data-testid="portal-ios-sheet">
      <div className="w-full max-w-md rounded-t-3xl bg-white p-6 sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-lg font-bold" style={{ color: PURPLE, fontFamily: "inherit" }}>Instalar o Cris Cliente</h3>
          <button onClick={onClose} className="text-gray-400"><X size={20} /></button>
        </div>
        {inApp ? (
          <p className="text-sm text-gray-600">
            Você está num navegador interno de outro aplicativo. Toque em <b>“Abrir no navegador”</b> (Safari ou Chrome) e tente instalar novamente por lá.
          </p>
        ) : ios ? (
          <ol className="space-y-3 text-sm text-gray-700">
            <li className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: PURPLE }}>1</span> Abra este site no <b>Safari</b>.</li>
            <li className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: PURPLE }}>2</span> Toque em <Share size={15} className="inline" /> <b>Compartilhar</b>.</li>
            <li className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: PURPLE }}>3</span> Escolha <Plus size={15} className="inline" /> <b>“Adicionar à Tela de Início”</b>.</li>
          </ol>
        ) : (
          <p className="text-sm text-gray-600">
            Seu navegador não ofereceu a instalação automática agora. Procure por <b>“Instalar aplicativo”</b> ou <b>“Adicionar à tela inicial”</b> no menu do navegador.
          </p>
        )}
        <button onClick={onClose} className="mt-5 w-full rounded-2xl py-2.5 text-sm font-semibold text-gray-500">Entendi</button>
      </div>
    </div>
  );
}

function OfflineScreen({ onRetry }) {
  return (
    <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center px-8 text-center" style={{ background: "#F6F1FB" }} data-testid="portal-offline">
      <div className="flex h-16 w-16 items-center justify-center rounded-full" style={{ background: LILAC }}>
        <WifiOff size={30} style={{ color: PURPLE }} />
      </div>
      <h2 className="mt-4 text-lg font-extrabold" style={{ color: PURPLE }}>Você está sem conexão</h2>
      <p className="mt-2 text-sm text-gray-500">Conecte-se à internet para consultar seus pagamentos e score.</p>
      <button onClick={onRetry} data-testid="portal-offline-retry"
        className="mt-5 rounded-2xl px-6 py-3 text-sm font-bold text-white" style={{ background: PURPLE }}>Tentar novamente</button>
    </div>
  );
}

function UpdateBanner({ reg, onApply, onDismiss }) {
  return (
    <div className="fixed inset-x-0 z-40 mx-auto max-w-2xl px-4" style={{ bottom: NAV_SPACE }} data-testid="portal-update-banner">
      <div className="flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-white shadow-lg" style={{ background: PURPLE }}>
        <span className="text-sm font-semibold">Uma nova versão está disponível.</span>
        <div className="flex items-center gap-2">
          <button onClick={() => onApply(reg)} data-testid="portal-update-apply" className="rounded-xl bg-white px-3 py-1.5 text-xs font-bold" style={{ color: PURPLE }}>Atualizar</button>
          <button onClick={onDismiss} className="text-white/70"><X size={16} /></button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------- ACESSO (login) */
export function PortalAccess() {
  const nav = useNavigate();
  const expired = !!useLocation().state?.expired;
  const [cpf, setCpf] = useState("");
  const [birth, setBirth] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fromWa = useLocation().search.includes("origem=whatsapp") || sessionStorage.getItem("cl_portal_origin") === "whatsapp";
  useEffect(() => {
    // Origem WhatsApp: conta a abertura 1x por sessao do navegador (sem CPF/dados no endereco).
    if (fromWa && !sessionStorage.getItem("cl_portal_origin")) {
      sessionStorage.setItem("cl_portal_origin", "whatsapp");
      papi.post("/portal/origin-event", { origin: "whatsapp", event: "portal_opened" }).catch(() => {});
    }
    if (hasPortalToken()) nav("/cliente", { replace: true });
  }, [nav, fromWa]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true); setPortalBusy(true);
    try {
      const { data } = await papi.post("/portal/login", { cpf, birth_date: birth, ...(fromWa ? { origin: "whatsapp" } : {}) });
      sessionStorage.removeItem("cl_portal_origin");
      savePortalToken(data.access_token);
      nav("/cliente", { replace: true });
    } catch (e2) {
      const st = e2?.response?.status;
      const detail = e2?.response?.data?.detail;
      if (st === 429) {
        setErr("Muitas tentativas. Aguarde alguns minutos e tente novamente.");
      } else if (st === 401) {
        setErr(typeof detail === "string" && detail
          ? detail
          : "Não foi possível validar seus dados. Confira as informações ou procure sua revendedora.");
      } else if (!e2?.response) {
        setErr("Falha de conexão. Verifique sua internet e tente novamente.");
      } else if (st >= 500) {
        setErr("Tivemos um problema técnico. Tente novamente em instantes.");
      } else {
        setErr("Não foi possível validar seus dados. Confira as informações ou procure sua revendedora.");
      }
    } finally { setLoading(false); setPortalBusy(false); }
  };

  const ready = cpf.replace(/\D/g, "").length === 11 && birth.replace(/\D/g, "").length === 8;

  return (
    <div className="flex min-h-[100dvh] w-full flex-col overflow-x-hidden"
      style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F8F4FC 55%, #F1EAFA 100%)", fontFamily: CC.sans, color: CC.ink }}
      data-testid="portal-access-screen">
      <main className="mx-auto w-full max-w-[440px] px-6 pt-[max(2.5rem,env(safe-area-inset-top))] sm:pt-14">
        <CrisClienteBrand />
        <h1 className="mt-8 text-[34px] font-semibold leading-[1.04] tracking-[-0.02em] min-[360px]:text-[38px] min-[400px]:text-[42px] sm:text-[46px]"
          style={{ fontFamily: CC.serif, color: CC.ink }}>
          Seu portal,<br />na palma da mão.
        </h1>
        <p className="mt-3 text-[16px] leading-snug sm:text-[17px]" style={{ color: "#3A2A55" }}>
          Acompanhe suas compras, parcelas e score.
        </p>
        {expired && (
          <div data-testid="portal-session-expired" className="mt-5 rounded-xl border border-[#E4D6F3] bg-[#F3EDFA] px-4 py-3 text-sm" style={{ color: CC.brand }}>
            Sua sessão expirou. Por segurança, entre novamente.
          </div>
        )}
        <form onSubmit={submit} className="mt-6 space-y-5" data-testid="portal-access-card" noValidate>
          <AccessField id="portal-cpf" label="CPF" icon={User} value={cpf} placeholder="Digite seu CPF"
            onChange={(v) => setCpf(maskCPF(v))} testid="portal-cpf-input" autoComplete="off" />
          <AccessField id="portal-birth" label="Data de nascimento" icon={CalendarDays} value={birth} placeholder="DD/MM/AAAA"
            onChange={(v) => setBirth(maskBirth(v))} testid="portal-birth-input" autoComplete="bday" />
          {err && (
            <div data-testid="portal-login-error" role="alert" className="flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <span>{err}</span>
            </div>
          )}
          <button type="submit" disabled={!ready || loading} data-testid="portal-login-submit"
            className="flex h-14 w-full items-center justify-center gap-3 rounded-xl text-[17px] font-semibold text-white shadow-[0_8px_20px_-10px_rgba(61,22,119,0.7)] transition-[opacity,transform] active:scale-[0.99] disabled:opacity-70"
            style={{ background: "linear-gradient(90deg, #4A1E86 0%, #5A2995 100%)" }}>
            {loading ? <Loader2 size={20} className="animate-spin" /> : null}
            Acessar meu portal
            {!loading && <ArrowRight size={20} strokeWidth={2} />}
          </button>
        </form>
        <div className="mt-4"><InstallButton variant="brand" /></div>
      </main>
      <BottomWaves>
        <p className="flex items-center justify-center gap-3 px-6 pb-[max(5.5rem,calc(env(safe-area-inset-bottom)+5rem))] pt-12 text-[14px] sm:text-[15px]"
          style={{ color: CC.brand }} data-testid="portal-access-help">
          <Shield size={22} strokeWidth={1.6} className="shrink-0" /> Dúvidas? Procure sua revendedora.
        </p>
      </BottomWaves>
    </div>
  );
}

function AccessField({ id, label, icon: Icon, value, onChange, placeholder, testid, autoComplete }) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-[15px] font-bold" style={{ color: CC.ink }}>{label}</label>
      <div className="flex h-14 items-center rounded-xl border bg-white transition-[border-color,box-shadow] focus-within:border-[#5A2995] focus-within:shadow-[0_0_0_3px_rgba(90,41,149,0.12)]"
        style={{ borderColor: CC.border }}>
        <span className="flex h-full w-14 shrink-0 items-center justify-center" style={{ color: CC.brand }}>
          <Icon size={22} strokeWidth={1.6} />
        </span>
        <span className="h-7 w-px shrink-0 bg-[#E2D7EE]" />
        <input id={id} value={value} onChange={(e) => onChange(e.target.value)} inputMode="numeric"
          placeholder={placeholder} data-testid={testid} autoComplete={autoComplete}
          className="h-full min-w-0 flex-1 rounded-r-xl bg-transparent px-4 text-[17px] tracking-wide outline-none placeholder:text-[#8A80A0]"
          style={{ color: CC.ink }} />
      </div>
    </div>
  );
}

/* ----------------------------------------------------------- shell + guard */
export function PortalRoot() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [state, setState] = useState("loading");
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);
  const [updateReg, setUpdateReg] = useState(null);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    checkWaitingUpdate((reg) => setUpdateReg(reg));
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    let alive = true;
    if (!hasPortalToken()) { nav("/cliente/acesso", { replace: true }); return; }
    papi.get("/portal/me").then((r) => { if (alive) { setMe(r.data); setState("ok"); } })
      .catch((e) => {
        if (!alive) return;
        if (!e?.response) { setState("ok"); return; } // sem rede: deixa a tela offline assumir
        clearPortalToken(); nav("/cliente/acesso", { replace: true, state: { expired: true } });
      });
    return () => { alive = false; };
  }, [nav]);

  usePortalPresence(state === "ok" && !!me, () => { clearPortalToken(); nav("/cliente/acesso", { replace: true, state: { expired: true } }); });

  if (offline) return <OfflineScreen onRetry={() => window.location.reload()} />;

  if (state !== "ok") {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: P.bg }}>
      <Loader2 className="animate-spin" style={{ color: PURPLE }} /></div>;
  }

  return (
    <div className="min-h-[100dvh] w-full overflow-x-hidden" style={{ background: P.bg, fontFamily: CC.sans, color: CC.ink, paddingBottom: NAV_SPACE }}
      data-testid="portal-shell">
      <Outlet context={me} />
      {updateReg && <UpdateBanner reg={updateReg} onApply={applyWaitingUpdate} onDismiss={() => setUpdateReg(null)} />}
      <BottomNav />
    </div>
  );
}

const Body = ({ children, overlap = false, testid }) => (
  <div className={`relative z-10 mx-auto w-full max-w-2xl space-y-4 px-4 sm:px-5 ${overlap ? "-mt-20" : "pt-4"}`} data-testid={testid}>
    {children}
  </div>
);

const Skeleton = ({ n = 3, h = "h-28" }) => (
  <div className="space-y-4">{Array.from({ length: n }).map((_, i) => <div key={i} className={`${h} animate-pulse rounded-2xl bg-white/80`} />)}</div>
);

const LoadError = ({ onRetry }) => (
  <Card className="p-5 text-sm" data-testid="portal-load-error">
    <span style={{ color: P.red }}>Não foi possível carregar.</span>{" "}
    <button onClick={onRetry} className="font-semibold underline" style={{ color: P.mid }}>Tentar novamente</button>
  </Card>
);

const segLabel = (data) => {
  const segs = data?.segments_available || [];
  if (data?.segment && data.segment !== "all") return segs.find((x) => x.bu === data.segment)?.label;
  return segs.map((x) => x.label).join(" + ");
};

// Resumo do servidor (mesmas funcoes de calculo), opcionalmente filtrado por segmento.
function useOverview(segment) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const load = useCallback(() => {
    setErr(false);
    papi.get("/portal/overview", { params: { segment } }).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [segment]);
  useEffect(() => { load(); }, [load]);
  return { data, err, load };
}

/* ----------------------------------------------------------- INÍCIO */
export function PortalHome() {
  const [segment, setSegment] = useState("all");
  const { data, err, load } = useOverview(segment);
  const [pix, setPix] = useState(null);

  const title = data ? `Olá, ${data.first_name}!` : "Olá!";
  return (
    <>
      <PortalHeader title={<span data-testid="portal-greeting">{title}</span>} subtitle="Suas compras em um só lugar.">
        {data && <HighlightCard label="Saldo em aberto" value={brl(data.summary.open_balance)} sub={segLabel(data)} icon={Wallet} testid="portal-open-balance" />}
      </PortalHeader>
      <Body testid="portal-home">
        {err && <LoadError onRetry={load} />}
        {!data && !err && <Skeleton />}
        {data && (
          <>
            {data.segments_available.length > 1 && (
              <SegmentSelect value={segment} onChange={setSegment} options={data.segments_available} />
            )}
            <div className="grid grid-cols-2 gap-3" data-testid="portal-summary">
              <div className="rounded-2xl p-4" style={{ background: P.redBg }} data-testid="portal-overdue-tile">
                <div className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: P.red }}><AlertTriangle size={17} /> Parcelas vencidas</div>
                <div className="mt-1 text-[26px] font-extrabold tabular-nums" style={{ color: P.red }}>{data.summary.overdue_installments}</div>
              </div>
              <div className="rounded-2xl p-4" style={{ background: P.lilac }} data-testid="portal-paid-tile">
                <div className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: P.lilacText }}><Wallet size={17} /> Pago até agora</div>
                <div className="mt-1 truncate text-[22px] font-extrabold tabular-nums sm:text-[24px]" style={{ color: P.deep }}>{brl(data.summary.total_paid)}</div>
              </div>
            </div>
            <NextDue nd={data.next_due} center={data.payment_center_enabled} onPay={(it) => setPix({ bu: it.bu, id: it.installment_id })} />
            <MiniScore score={data.score} />
            <RowLink to="/cliente/recibos" icon={FileText} title="Meus recibos" sub="Acesse seus comprovantes" testid="portal-receipts-link" />
          </>
        )}
      </Body>
      {pix && <PixModal open onClose={() => { setPix(null); load(); }} bu={pix.bu} installmentId={pix.id} />}
    </>
  );
}

function SegmentSelect({ value, onChange, options }) {
  return (
    <div>
      <label htmlFor="portal-segment" className="mb-1.5 block text-[14px] font-semibold" style={{ color: CC.ink }}>Visualizar segmento</label>
      <div className="relative">
        <select id="portal-segment" value={value} onChange={(e) => onChange(e.target.value)} data-testid="portal-segment-select"
          className="h-12 w-full appearance-none rounded-xl border bg-white pl-4 pr-10 text-[16px] outline-none focus:border-[#5A2995]"
          style={{ borderColor: P.line, color: CC.ink }}>
          <option value="all">Todos os segmentos</option>
          {options.map((o) => <option key={o.bu} value={o.bu}>{o.label}</option>)}
        </select>
        <ChevronDown size={20} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" style={{ color: P.muted }} />
      </div>
    </div>
  );
}

function NextDue({ nd, center, onPay }) {
  const [open, setOpen] = useState(false);
  if (!nd) {
    return (
      <Card className="p-5 text-center text-[15px]" data-testid="portal-next-due">
        <CheckCircle2 size={26} className="mx-auto mb-1.5" style={{ color: P.green }} />
        Você está em dia. Nenhuma parcela em aberto.
      </Card>
    );
  }
  const tone = nd.overdue ? instTone("vencida") : instTone("a_vencer");
  const many = nd.count > 1;
  return (
    <Card className="p-5" data-testid="portal-next-due">
      <div className="flex items-center justify-between">
        <div className="text-[17px] font-bold">Próximo vencimento</div>
        <Badge tone={tone} small testid="portal-next-status">{nd.overdue ? `Atrasado · ${nd.overdue_days} dia${nd.overdue_days === 1 ? "" : "s"}` : "A vencer"}</Badge>
      </div>
      <div className="mt-1 text-[30px] font-extrabold tabular-nums" style={{ color: nd.overdue ? P.red : CC.ink }} data-testid="portal-next-amount">{brl(nd.total)}</div>
      <div className="text-[14px]" style={{ color: P.muted }} data-testid="portal-next-meta">
        {nd.overdue ? "Venceu em" : "Vence em"} {formatDate(nd.due_date)} · {nd.count} parcela{many ? "s" : ""}
      </div>
      {nd.by_segment?.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="portal-next-by-segment">
          {nd.by_segment.map((b) => (
            <span key={b.bu} className="rounded-full px-3 py-1 text-[12px] font-bold" style={{ background: b.bu === "eletros" ? "#F6E9CF" : "#EFE3FB", color: b.bu === "eletros" ? "#7A5617" : "#5A2995" }}>
              {b.segment}: {brl(b.total)} · {b.count}
            </span>
          ))}
        </div>
      )}
      {many && (
        <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid="portal-next-toggle"
          className="mt-2 flex items-center gap-1 text-[13px] font-bold" style={{ color: P.mid }}>
          {open ? "Ocultar detalhamento" : "Ver detalhamento"} <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      <div className={`grid transition-[grid-template-rows] duration-300 ${open || !many ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="mt-2 divide-y divide-[#ECE4F5]" data-testid="portal-next-items">
            {nd.items.map((it) => (
              <div key={it.installment_id} className="flex items-center justify-between gap-3 py-2 text-[13px]">
                <span className="min-w-0">
                  <span className="block truncate font-semibold" style={{ color: CC.ink }}>{it.segment} · Compra #{it.sale_number} · Parcela {it.number}</span>
                  {it.reseller_name && <span className="block truncate" style={{ color: P.muted }}>Revendedora: {it.reseller_name}</span>}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="font-extrabold tabular-nums" style={{ color: CC.ink }}>{brl(it.updated_total)}</span>
                  {many && !center && (
                    <button onClick={() => onPay(it)} data-testid={`portal-next-pay-${it.installment_id}`}
                      className="rounded-lg border px-2.5 py-1 text-[12px] font-bold" style={{ borderColor: P.mid, color: P.mid }}>Pagar</button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {!many && (
        <PixButton onClick={() => onPay(nd.items[0])} testid="portal-pay-next">Pagar com PIX</PixButton>
      )}
      {many && center && (
        <Link to={`/cliente/compras?aba=pagamentos&data=${nd.due_date}`} data-testid="portal-pay-next"
          className="mt-4 flex h-12 w-full items-center justify-center gap-2.5 rounded-xl text-[16px] font-bold text-white"
          style={{ background: "linear-gradient(90deg, #4A1E86 0%, #5A2995 100%)" }}>
          <QrCode size={18} /> Pagar vencimento com PIX
        </Link>
      )}
    </Card>
  );
}

const PixButton = ({ onClick, testid, children }) => (
  <button onClick={onClick} data-testid={testid}
    className="mt-4 flex h-12 w-full items-center justify-center gap-2.5 rounded-xl text-[16px] font-bold text-white transition-transform active:scale-[0.99]"
    style={{ background: "linear-gradient(90deg, #4A1E86 0%, #5A2995 100%)" }}>
    <QrCode size={18} /> {children}
  </button>
);

function MiniScore({ score }) {
  if (!score) return null;
  if (!score.available) {
    return <Card className="p-4 text-[14px]" data-testid="portal-score-card" style={{ color: P.muted }}>Meu score CRIS CREDIT · ainda não disponível</Card>;
  }
  return (
    <Card className="flex items-center gap-4 p-4" data-testid="portal-score-card">
      <Gauge size={30} strokeWidth={1.6} className="shrink-0" style={{ color: P.mid }} />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold">Meu score CRIS CREDIT</div>
        <div className="mt-0.5 text-[26px] font-extrabold tabular-nums leading-tight" data-testid="portal-mini-score">
          {score.score} <span className="text-[17px] font-semibold" style={{ color: P.muted }}>/ 1000</span>
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <Badge tone={riskTone(score.risk)} small testid="portal-mini-score-label">{score.label}</Badge>
        <Link to="/cliente/score" className="text-[13px] font-bold underline underline-offset-2" style={{ color: P.mid }} data-testid="portal-see-score">Ver score →</Link>
      </div>
    </Card>
  );
}

/* ----------------------------------------------------------- COMPRAS */
const PAGE = 30;
export function PortalPurchases() {
  const params = new URLSearchParams(useLocation().search);
  const nav = useNavigate();
  const tab = params.get("aba") === "pagamentos" ? "pagamentos" : "compras";
  const { data: ov } = useOverview("all");
  const center = !!ov?.payment_center_enabled;
  const setTab = (t) => nav(t === "pagamentos" ? "/cliente/compras?aba=pagamentos" : "/cliente/compras", { replace: true });
  if (center && tab === "pagamentos") {
    return (
      <>
        <PortalHeader title="Central de Pagamentos" subtitle="Seus vencimentos em um só lugar.">
          <PurchaseTabs tab={tab} onChange={setTab} />
        </PortalHeader>
        <Body><PaymentCenter focusDate={params.get("data")} /></Body>
      </>
    );
  }
  return <PurchasesList tabs={center ? <PurchaseTabs tab={tab} onChange={setTab} /> : null} />;
}

const PurchaseTabs = ({ tab, onChange }) => (
  <div className="grid grid-cols-2 gap-1 rounded-2xl bg-white/10 p-1" role="tablist" data-testid="portal-purchase-tabs">
    {[["compras", "Minhas compras"], ["pagamentos", "Central de Pagamentos"]].map(([k, l]) => (
      <button key={k} role="tab" aria-selected={tab === k} onClick={() => onChange(k)} data-testid={`portal-tab-${k}`}
        className={`h-10 rounded-xl text-[13px] font-bold transition-colors ${tab === k ? "bg-white text-[#3E1874]" : "text-white/85"}`}>{l}</button>
    ))}
  </div>
);

function PurchasesList({ tabs }) {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [seg, setSeg] = useState("all");
  const [shown, setShown] = useState(PAGE);
  const { data } = useOverview(seg);
  const loadRows = useCallback(() => {
    setFailed(false);
    papi.get("/portal/purchases").then((r) => setRows(r.data)).catch(() => setFailed(true));
  }, []);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { setShown(PAGE); }, [seg]);

  const segs = data?.segments_available || [];
  const list = (rows || []).filter((p) => seg === "all" || p.bu === seg);
  return (
    <>
      <PortalHeader title="Minhas compras" subtitle="Acompanhe cada compra.">
        {tabs && <div className="mb-3">{tabs}</div>}
        {data && <HighlightCard label="Total em compras" value={brl(data.summary.total_bought)} sub={segLabel(data)} icon={ShoppingBag} testid="portal-total-bought" />}
      </PortalHeader>
      <Body testid="portal-purchases-list">
        {segs.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" data-testid="portal-segment-chips">
            {[{ bu: "all", label: "Todas" }, ...segs].map((o) => {
              const on = seg === o.bu;
              return (
                <button key={o.bu} role="tab" aria-selected={on} onClick={() => setSeg(o.bu)} data-testid={`portal-chip-${o.bu}`}
                  className="h-10 shrink-0 rounded-full border px-5 text-[14px] font-semibold transition-colors"
                  style={on ? { background: P.mid, borderColor: P.mid, color: "#fff" } : { background: "#fff", borderColor: P.line, color: CC.ink }}>
                  {o.label}
                </button>
              );
            })}
          </div>
        )}
        {failed && <LoadError onRetry={loadRows} />}
        {!rows && !failed && <Skeleton n={3} h="h-36" />}
        {rows && list.length === 0 && <Card className="p-8 text-center text-[15px]" style={{ color: P.muted }}>Você ainda não tem compras.</Card>}
        {list.slice(0, shown).map((p) => <PurchaseCard key={`${p.bu}-${p.id}`} p={p} />)}
        {list.length > shown && (
          <button onClick={() => setShown(shown + PAGE)} data-testid="portal-purchases-more"
            className="h-12 w-full rounded-xl border bg-white text-[15px] font-semibold" style={{ borderColor: P.line, color: P.mid }}>
            Mostrar mais ({list.length - shown})
          </button>
        )}
      </Body>
    </>
  );
}

function PurchaseCard({ p }) {
  return (
    <Link to={`/cliente/compras/${p.bu}/${p.id}`} data-testid={`portal-purchase-${p.id}`}
      className="block rounded-2xl border bg-white p-4 transition-colors hover:bg-[#FBF8FE]" style={{ borderColor: P.line }}>
      <div className="flex items-center justify-between gap-2">
        <SegmentTag bu={p.bu} label={p.segment} />
        <span className="text-[13px]" style={{ color: P.muted }}>{formatDate(p.date)}</span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[17px] font-bold">Compra #{p.number}</div>
          {p.reseller_name && <div className="truncate text-[13px]" style={{ color: P.muted }}>Revendedora: {p.reseller_name}</div>}
        </div>
        <ChevronRight size={20} className="shrink-0" style={{ color: P.muted }} />
      </div>
      {p.status === "cancelled" && <div className="mt-2 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-500">Cancelada</div>}
      <div className="mt-3 grid grid-cols-2 border-t pt-3" style={{ borderColor: P.line }}>
        <div className="pr-3">
          <div className="text-[12px]" style={{ color: P.muted }}>Valor da compra</div>
          <div className="text-[16px] font-extrabold tabular-nums">{brl(p.total)}</div>
        </div>
        <div className="border-l pl-3" style={{ borderColor: P.line }}>
          <div className="text-[12px]" style={{ color: P.muted }}>Saldo atualizado</div>
          <div className="text-[16px] font-extrabold tabular-nums">{brl(p.balance)}</div>
        </div>
      </div>
    </Link>
  );
}

/* ----------------------------------------------------------- COMPRA (detalhe) */
export function PortalPurchaseDetail() {
  const { bu, id } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [pix, setPix] = useState(null);

  const load = useCallback(() => {
    papi.get(`/portal/purchases/${bu}/${id}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [bu, id]);
  useEffect(() => { load(); }, [load]);

  const contact = async () => {
    try { const { data } = await papi.get(`/portal/contact/${bu}/${id}`); window.open(data.whatsapp_url, "_blank"); }
    catch { /* indisponivel */ }
  };

  const cancelled = p?.status === "cancelled";
  return (
    <>
      <PortalHeader title={p ? `Compra #${p.number}` : "Compra"} subtitle={p ? `${p.segment} · ${formatDate(p.date)}` : ""} overlap>
        <Link to="/cliente/compras" className="inline-flex items-center gap-1 text-[14px] font-semibold text-white/85" data-testid="portal-back">
          <ChevronLeft size={16} /> Minhas compras
        </Link>
      </PortalHeader>
      <Body overlap testid="portal-purchase-detail">
        {notFound && <Card className="p-8 text-center text-[15px]" style={{ color: P.muted }}>Compra não encontrada.</Card>}
        {!p && !notFound && <Skeleton n={2} h="h-40" />}
        {p && (
          <>
            <Card className="p-5">
              <div className="flex items-center justify-between gap-2">
                <SegmentTag bu={p.bu} label={p.segment} />
                {cancelled && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-500">Cancelada</span>}
              </div>
              {p.reseller_name && <div className="mt-2 text-[14px]" style={{ color: P.muted }}>Revendedora responsável: {p.reseller_name}</div>}
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <Stat k="Total" v={brl(p.total)} />
                <Stat k="Pago" v={brl(p.paid)} color={P.green} />
                <Stat k="Saldo" v={brl(p.balance)} color={P.mid} />
              </div>
              <button onClick={contact} data-testid="portal-contact-reseller"
                className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-xl border-[1.5px] text-[14px] font-bold" style={{ borderColor: "#25D366", color: "#128C3E" }}>
                <MessageCircle size={16} /> Falar com minha revendedora
              </button>
            </Card>
            <Card className="p-5">
              <div className="text-[16px] font-bold">Produtos</div>
              <div className="mt-2 divide-y divide-[#ECE4F5]">
                {(p.products || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 py-2 text-[14px]">
                    <span className="min-w-0 break-words">{it.name} <span style={{ color: P.muted }}>×{it.qty}</span></span>
                    <span className="shrink-0 tabular-nums" style={{ color: P.muted }}>{brl((it.price || 0) * (it.qty || 1))}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5">
              <div className="text-[16px] font-bold">Parcelas</div>
              <div className="mt-2 space-y-2">
                {(p.installments || []).map((i) => {
                  const tone = instTone(i.status);
                  const payable = i.balance > 0.01 && !cancelled;
                  return (
                    <div key={i.id} className="rounded-xl border p-3" style={{ borderColor: P.line }} data-testid={`portal-installment-${i.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[14px] font-semibold">#{i.number} · {formatDate(i.due_date)}</div>
                        <Badge tone={tone} small>{tone.label}</Badge>
                      </div>
                      <div className="mt-1.5 flex items-center justify-between gap-2 text-[14px]">
                        <span style={{ color: P.muted }}>
                          {brl(i.updated_total)}
                          {(i.fine > 0 || i.interest > 0) && <span className="text-[11px]" style={{ color: P.red }}> (inclui encargos)</span>}
                        </span>
                        {payable && (
                          <button onClick={() => setPix({ bu: i.bu, id: i.id })} data-testid={`portal-pay-${i.id}`}
                            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-bold text-white" style={{ background: P.mid }}>
                            <QrCode size={13} /> Pagar PIX
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </>
        )}
      </Body>
      {pix && <PixModal open onClose={() => { setPix(null); load(); }} bu={pix.bu} installmentId={pix.id} />}
    </>
  );
}

const Stat = ({ k, v, color }) => (
  <div className="min-w-0">
    <div className="text-[12px]" style={{ color: P.muted }}>{k}</div>
    <div className="truncate text-[15px] font-extrabold tabular-nums" style={{ color: color || CC.ink }}>{v}</div>
  </div>
);

/* ----------------------------------------------------------- SCORE (CRIS CREDIT 2.0) */
export function PortalScore() {
  return <ScoreScreen />;
}

/* ----------------------------------------------------------- RECIBOS */
export function PortalReceipts() {
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { papi.get("/portal/receipts").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  const open = async (r) => {
    try { const { data } = await papi.get(`/portal/receipts/${r.bu}/${r.id}`); setDetail(data); }
    catch { /* */ }
  };

  return (
    <>
      <PortalHeader title="Meus recibos" subtitle="Seus comprovantes de pagamento." overlap>
        <Link to="/cliente/conta" className="inline-flex items-center gap-1 text-[14px] font-semibold text-white/85" data-testid="portal-back">
          <ChevronLeft size={16} /> Minha conta
        </Link>
      </PortalHeader>
      <Body overlap testid="portal-receipts">
        {!rows && <Skeleton n={2} h="h-20" />}
        {rows && rows.length === 0 && <Card className="p-8 text-center text-[15px]" style={{ color: P.muted }}>Nenhum pagamento confirmado ainda.</Card>}
        {rows?.map((r) => (
          <button key={`${r.bu}-${r.id}`} onClick={() => open(r)} data-testid={`portal-receipt-${r.id}`}
            className="flex w-full items-center gap-4 rounded-2xl border bg-white p-4 text-left transition-colors hover:bg-[#FBF8FE]" style={{ borderColor: P.line }}>
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: P.greenBg, color: P.green }}><Check size={20} /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-[16px] font-extrabold tabular-nums">{brl(r.amount)}</span>
              <span className="block truncate text-[13px]" style={{ color: P.muted }}>{r.segment} · {r.method?.toUpperCase()} · {formatDate(r.paid_at)}</span>
            </span>
            <FileText size={20} className="shrink-0" style={{ color: P.muted }} />
          </button>
        ))}
      </Body>
      {detail && <ReceiptModal detail={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

/* ----------------------------------------------------------- CONTA */
export function PortalConta() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  useEffect(() => { papi.get("/portal/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  const logout = async () => {
    try { await papi.post("/portal/logout"); } catch { /* */ }
    clearPortalToken(); nav("/cliente/acesso", { replace: true });
  };
  return (
    <>
      <PortalHeader title="Minha conta" overlap />
      <Body overlap testid="portal-conta">
        <Card className="flex items-center gap-4 p-5" data-testid="portal-profile">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-[26px] font-bold" style={{ background: P.lilac, color: P.mid }}>
            {(me?.name || "C")[0].toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="truncate text-[19px] font-bold" data-testid="portal-profile-name">{me?.name || "Cliente"}</div>
            <div className="text-[14px]" style={{ color: P.muted }}>{me?.wallets > 1 ? `${me.wallets} cadastros vinculados` : "Cliente Cris Cliente"}</div>
          </div>
        </Card>
        <RowLink to="/cliente/recibos" icon={FileText} title="Meus recibos" sub="Acesse seus comprovantes" testid="portal-conta-receipts" />
        <InstallButton variant="row" />
        <button onClick={logout} data-testid="portal-logout"
          className="flex h-14 w-full items-center justify-center gap-2.5 rounded-2xl border-[1.5px] bg-white text-[16px] font-bold transition-colors hover:bg-red-50"
          style={{ borderColor: "#E9A3AE", color: P.red }}>
          <LogOut size={20} /> Sair da conta
        </button>
        <div className="flex flex-col items-center pb-4 pt-8 text-center" data-testid="portal-conta-brand">
          <img src="/cliente-icons/symbol-light.png" alt="" aria-hidden="true" className="h-14 w-auto" />
          <div className="mt-1.5 text-[24px] font-semibold" style={{ fontFamily: CC.serif, color: CC.brand }}>Cris Cliente</div>
          <div className="mt-1 text-[9.5px] font-bold uppercase tracking-[0.28em]" style={{ color: CC.gold }}>Banco das Revendedoras</div>
          <div className="mt-2 text-[13px]" style={{ color: P.muted }}>Suas compras em um só lugar.</div>
        </div>
      </Body>
    </>
  );
}

function ReceiptModal({ detail, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose}>
      <div className="w-full max-w-md rounded-t-3xl bg-white p-6 sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div id="portal-receipt-print" className="rounded-2xl border border-gray-100 p-5">
          <div className="text-center">
            <img src="/logo-full.png" alt="" className="mx-auto h-12 w-12 rounded-lg" />
            <div className="mt-2 text-lg font-extrabold" style={{ color: PURPLE }}>Recibo de pagamento</div>
            <div className="text-xs text-gray-400">{detail.segment} · Cris Lingerie</div>
          </div>
          <div className="mt-4 space-y-1.5 text-sm">
            <Row k="Cliente" v={detail.client_name} />
            <Row k="Compra" v={`#${detail.sale_number ?? "-"}`} />
            <Row k="Parcela" v={`#${detail.installment_number ?? "-"}`} />
            <Row k="Método" v={(detail.method || "").toUpperCase()} />
            <Row k="Data" v={formatDate(detail.paid_at)} />
            <Row k="Revendedora" v={detail.reseller_name || "-"} />
            <div className="my-2 border-t border-dashed border-gray-200" />
            <Row k="Valor pago" v={brl(detail.amount)} strong />
            <div className="pt-1 text-[10px] text-gray-400">ID do pagamento: {detail.id}</div>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={() => window.print()} data-testid="portal-receipt-print-btn"
            className="flex flex-1 items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold text-white" style={{ background: PURPLE }}>
            <Printer size={16} /> Imprimir / Salvar
          </button>
          <button onClick={onClose} className="rounded-2xl px-4 py-3 text-sm font-semibold text-gray-500">Fechar</button>
        </div>
      </div>
    </div>
  );
}
function Row({ k, v, strong }) {
  return <div className="flex justify-between"><span className="text-gray-400">{k}</span>
    <span className={strong ? "font-extrabold" : "font-medium"} style={strong ? { color: PURPLE } : {}}>{v}</span></div>;
}
